import { FolderPlus, Loader2 } from 'lucide-react';
import { useId, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';

import { useCreateCategory, useListCategories } from '../api';

/**
 * "New category" beside "Add Service". Service categories could only be
 * created by an import or the website scan; this is the hand-made way in.
 */
export function NewServiceCategoryDialog() {
  const formId = useId();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const { createCategoryAsync, isCreating } = useCreateCategory();
  const { categories, isLoading, isError } = useListCategories();

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!name.trim()) {
      toast.error('Name is required');
      return;
    }
    // New categories go after the existing ones, not on top of them. Submit
    // waits for the list, so an empty list really means "no categories yet".
    const intent = {
      name: name.trim(),
      sortOrder: categories.reduce(
        (last, category) => Math.max(last, category.sortOrder + 1),
        0
      ),
      isActive: true,
    };
    try {
      await createCategoryAsync(intent);
      setOpen(false);
      setName('');
    } catch {
      // The hook already shows an error toast.
    }
  };

  return (
    <Dialog
      onOpenChange={(next) => {
        if (isCreating) return;
        setOpen(next);
        if (!next) setName('');
      }}
      open={open}
    >
      <DialogTrigger asChild>
        <Button className="gap-1.5" size="sm" variant="outline">
          <FolderPlus className="size-4" />
          <span className="hidden sm:inline">New Category</span>
          <span className="sr-only sm:hidden">New category</span>
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>New category</DialogTitle>
        </DialogHeader>
        <form id={formId} onSubmit={handleSubmit}>
          <FieldGroup>
            <Field>
              <FieldLabel htmlFor={`${formId}-name`}>Name</FieldLabel>
              <Input
                autoFocus
                id={`${formId}-name`}
                maxLength={100}
                onChange={(event) => setName(event.target.value)}
                placeholder="e.g. Facials"
                required
                value={name}
              />
            </Field>
          </FieldGroup>
          {isError && (
            <p className="mt-2 text-destructive text-sm" role="alert">
              Couldn’t load your existing categories, so this one can’t be
              placed after them. Refresh and try again.
            </p>
          )}
        </form>
        <DialogFooter>
          <Button
            disabled={isCreating}
            onClick={() => setOpen(false)}
            type="button"
            variant="outline"
          >
            Cancel
          </Button>
          <Button
            disabled={isCreating || isLoading || isError}
            form={formId}
            type="submit"
          >
            {isCreating && <Loader2 className="size-4 animate-spin" />}
            Create category
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
